import { Injectable, NotFoundException } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { Hero, HeroDocument } from '../schemas/hero.schema';
import { CreateHeroDto } from '../dto/create-hero.dto';
import { UpdateHeroDto } from '../dto/update-hero.dto';
import { HeroQueryDto } from '../dto/hero-query.dto';

@Injectable()
export class HeroService {
    constructor(
        @InjectModel(Hero.name) private heroModel: Model<HeroDocument>,
    ) { }

    async create(createHeroDto: CreateHeroDto): Promise<Hero> {
        const createdHero = new this.heroModel(createHeroDto);
        return createdHero.save();
    }

    async findAll(query: HeroQueryDto) {
        const { page = 1, limit = 10, isAlways, isActive, search, isAll } = query;
        const skip = (page - 1) * limit;

        // Build filter object
        const filter: any = {};

        if (isAlways !== undefined) {
            filter.isAlways = isAlways;
        }

        if (isActive !== undefined) {
            filter.isActive = isActive;
        }

        if (search) {
            filter.title = { $regex: search, $options: 'i' };
        }

        // Special filtering logic for active heroes
        const currentDate = new Date();
        let dateFilter = {};
        // date filter when isAll == false
        if (isAll === false) {
            dateFilter = {
                $or: [
                    { isAlways: true },
                    {
                        isAlways: false,
                        startDate: { $lte: currentDate },
                        endDate: { $gte: currentDate }
                    }
                ]
            };
        }
        // If no specific filters are applied, use the date-based logic
        if (Object.keys(filter).length === 0) {
            Object.assign(filter, dateFilter);
        }

        const [heroes, total] = await Promise.all([
            this.heroModel
                .find(filter)
                .sort({ order: 1 })
                .skip(skip)
                .limit(limit)
                .exec(),
            this.heroModel.countDocuments(filter).exec(),
        ]);

        const pages = Math.ceil(total / limit);

        return {
            heroes,
            pagination: {
                page,
                limit,
                total,
                pages,
            },
        };
    }

    async findOne(id: string): Promise<Hero> {
        const hero = await this.heroModel.findById(id).exec();
        if (!hero) {
            throw new NotFoundException(`Hero with ID ${id} not found`);
        }
        return hero;
    }

    async update(id: string, updateHeroDto: UpdateHeroDto): Promise<Hero> {
        const updatedHero = await this.heroModel
            .findByIdAndUpdate(id, updateHeroDto, { new: true })
            .exec();
        if (!updatedHero) {
            throw new NotFoundException(`Hero with ID ${id} not found`);
        }
        return updatedHero;
    }

    async remove(id: string): Promise<Hero> {
        const deletedHero = await this.heroModel.findByIdAndDelete(id).exec();
        if (!deletedHero) {
            throw new NotFoundException(`Hero with ID ${id} not found`);
        }
        return deletedHero;
    }

    // Get active heroes for frontend display
    async getActiveHeroes() {
        const currentDate = new Date();

        return this.heroModel.find({
            isActive: true,
            $or: [
                { isAlways: true },
                {
                    isAlways: false,
                    startDate: { $lte: currentDate },
                    endDate: { $gte: currentDate }
                }
            ]
        }).sort({ order: 1 }).exec();
    }
} 